import React, { useMemo, useState } from "react";
import api, { formatApiError, uploadFileToStorage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Files, Loader2, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const NOISE = /\b(stems?|track ?outs?|trackouts?|master|untagged|final|wav|zip|mix|v\d+)\b/g;
export const matchKey = (s) => s.toLowerCase()
  .replace(/\.[a-z0-9]{2,4}$/, "")
  .replace(/\b\d{2,3}\s*bpm\b/g, " ")
  .replace(/\$?\b\d+\.\d{1,2}\b/g, " ")
  .replace(/[_\-.]+/g, " ")
  .replace(NOISE, " ")
  .replace(/[^a-z0-9]+/g, "");

export function autoMatch(filename, tracks) {
  const base = filename.replace(/\.[a-z0-9]{2,4}$/i, "");
  const keys = [base, ...base.split(/\s+-\s+|\s*\|\s*/)].map(matchKey).filter(Boolean);
  const keyed = tracks.map((t) => ({ t, k: matchKey(t.title) })).filter((x) => x.k.length >= 2);
  const exact = keyed.find((x) => keys.includes(x.k));
  if (exact) return exact.t.id;
  const full = matchKey(base);
  const partial = keyed.filter((x) => x.k.length >= 3 && full.includes(x.k)).sort((a, b) => b.k.length - a.k.length)[0];
  return partial ? partial.t.id : "";
}

const kindFor = (name) => (/\.(zip|rar|7z)$/i.test(name) || /stem|track ?out/i.test(name) ? "stems" : "wav");
const ACCEPT = /\.(wav|zip|rar|7z)$/i;
const PARALLEL = 3;

function Row({ row, idx, tracks, onChange, disabled }) {
  const status = row.state === "done" ? <Check className="w-4 h-4 text-[#22c55e]" />
    : row.state === "error" ? <X className="w-4 h-4 text-[#ef4444]" />
    : row.state === "uploading" ? <span className="text-xs font-mono text-[#e2ff31]">{row.pct}%</span> : null;
  return (
    <div data-testid={`bulk-row-${idx}`} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1.2fr_110px_1.4fr_40px] gap-2 items-center py-2 border-b border-[#1e1e1e] last:border-0">
      <p className="text-sm truncate" title={row.file.name}>{row.file.name}</p>
      <Select value={row.kind} onValueChange={(v) => onChange(idx, { kind: v })} disabled={disabled}>
        <SelectTrigger data-testid={`bulk-kind-${idx}`} className="h-8 bg-[#0a0a0a] border-[#2a2a2a] text-xs"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6]">
          <SelectItem value="wav" disabled={!/\.wav$/i.test(row.file.name)}>WAV master</SelectItem>
          <SelectItem value="stems">Stems</SelectItem>
        </SelectContent>
      </Select>
      <Select value={row.trackId || "skip"} onValueChange={(v) => onChange(idx, { trackId: v === "skip" ? "" : v })} disabled={disabled}>
        <SelectTrigger data-testid={`bulk-match-${idx}`} className={`h-8 bg-[#0a0a0a] text-xs ${row.trackId ? "border-[#2a2a2a]" : "border-[#f59e0b]/60 text-[#f59e0b]"}`}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6] max-h-72">
          <SelectItem value="skip">— No match (skip) —</SelectItem>
          {tracks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}{t.sold ? " · sold" : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      <div data-testid={`bulk-status-${idx}`} className="flex justify-center">{status}</div>
    </div>
  );
}

export const BulkAttach = ({ tracks, onDone }) => {
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const sorted = useMemo(() => [...tracks].sort((a, b) => a.title.localeCompare(b.title)), [tracks]);

  const addFiles = (list) => {
    const files = Array.from(list).filter((f) => ACCEPT.test(f.name));
    if (files.length < list.length) toast.error("Only .wav and .zip/.rar/.7z files can be attached");
    setRows((prev) => [...prev, ...files.map((file) => ({ file, kind: kindFor(file.name), trackId: autoMatch(file.name, tracks), state: "ready", pct: 0 }))]);
  };
  const update = (i, patch) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const ready = rows.filter((r) => r.trackId && r.state !== "done");
  const unmatched = rows.filter((r) => !r.trackId && r.state !== "done").length;

  const run = async () => {
    setRunning(true);
    let ok = 0; const emailed = new Set();
    const queue = rows.map((r, i) => [r, i]).filter(([r]) => r.trackId && r.state !== "done");
    const worker = async () => {
      while (queue.length) {
        const [r, i] = queue.shift();
        update(i, { state: "uploading", pct: 0 });
        try {
          const ref = await uploadFileToStorage(r.file, r.kind, (p) => update(i, { pct: p }));
          const { data } = await api.post(`/admin/tracks/${r.trackId}/attach`, {
            kind: r.kind, path: ref.path, filename: ref.filename || r.file.name, content_type: ref.content_type || r.file.type,
          });
          data.notified.forEach((e) => emailed.add(e));
          update(i, { state: "done", pct: 100 }); ok += 1;
        } catch (e) {
          update(i, { state: "error" });
          toast.error(`${r.file.name}: ${formatApiError(e.response?.data?.detail) || "upload failed"}`);
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length) }, worker));
    setRunning(false);
    if (ok) toast.success(`Attached ${ok} file${ok > 1 ? "s" : ""}${emailed.size ? ` · emailed ${[...emailed].join(", ")}` : ""}`);
    onDone();
  };

  return (
    <div data-testid="bulk-attach" className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-6">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Files className="w-5 h-5 text-[#e2ff31]" /> Bulk attach WAVs &amp; stems</h2>
      <p className="text-xs text-[#6b7280] mt-1">Drop many files at once — each one is matched to a beat by its name. Fix any match before uploading. Buyers of sold beats get emailed.</p>
      <label data-testid="bulk-dropzone"
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); addFiles(e.dataTransfer.files); }}
        className={`mt-4 flex flex-col items-center justify-center rounded-xl border-2 border-dashed py-6 cursor-pointer transition-colors ${over ? "border-[#e2ff31] bg-[#e2ff31]/5" : "border-[#2a2a2a] hover:border-[#4b5563]"}`}>
        <span className="text-sm">Drop .wav / .zip files here or <span className="text-[#e2ff31]">browse</span></span>
        <input data-testid="bulk-input" type="file" multiple accept=".wav,.zip,.rar,.7z" className="hidden" disabled={running}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </label>
      {rows.length > 0 && (
        <>
          <div className="mt-4">{rows.map((r, i) => <Row key={`${r.file.name}-${i}`} row={r} idx={i} tracks={sorted} onChange={update} disabled={running || r.state === "done"} />)}</div>
          {unmatched > 0 && (
            <p data-testid="bulk-unmatched" className="mt-3 text-xs text-[#f59e0b] flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> {unmatched} file{unmatched > 1 ? "s" : ""} not matched — pick a beat or they'll be skipped
            </p>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button data-testid="bulk-clear" variant="outline" disabled={running} onClick={() => setRows([])}
              className="rounded-full border-[#2a2a2a] bg-transparent hover:bg-[#1e1e1e]">Clear</Button>
            <Button data-testid="bulk-submit" onClick={run} disabled={running || !ready.length}
              className="rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : `Attach ${ready.length} file${ready.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
